<script setup>
import {
  computed,
  onMounted,
  nextTick,
  onUnmounted,
  ref,
  useTemplateRef,
  inject,
} from 'vue';
import { useWindowSize, useElementBounding, useScrollLock } from '@vueuse/core';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { layoutPx } from 'dashboard/helper/dashboardZoom';

const props = defineProps({
  // Aceleres: em espaco VISUAL — `clientX`/`clientY` do rato, ou um ponto tirado
  // de um `getBoundingClientRect()`. Nunca `pageX`/`pageY`. Ver helper/dashboardZoom.js.
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
  mobileSheet: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const MOBILE_SHEET_MIN_HEIGHT = 55;
const MOBILE_SHEET_DISMISS_HEIGHT = 120;
const mobileSheetHeight = ref(null);
let dragPointerId = null;
let dragStartY = 0;
let dragStartHeight = 0;
let dragMoved = false;

const elementToLock = inject('contextMenuElementTarget', null);

const menuRef = useTemplateRef('menuRef');

const mobileSheetStyle = computed(() =>
  mobileSheetHeight.value === null
    ? undefined
    : { height: layoutPx(mobileSheetHeight.value) }
);

const setMobileSheetHeight = height => {
  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  mobileSheetHeight.value = Math.min(
    Math.max(height, MOBILE_SHEET_MIN_HEIGHT),
    viewportHeight - 12
  );
};

const scrollLockElement = computed(() => {
  if (!elementToLock?.value) return null;
  return elementToLock.value?.$el;
});

const isLocked = useScrollLock(scrollLockElement);

// Aceleres: `useWindowSize` (innerWidth/innerHeight) e `useElementBounding`
// (getBoundingClientRect) reportam AMBOS em espaco visual, tal como o
// `clientX`/`clientY` que chega em `props.x`/`props.y`. Por isso toda a
// aritmetica abaixo fica coerente sem conversao nenhuma; so a escrita final em
// CSS precisa de voltar a layout. Ver helper/dashboardZoom.js.
const { width: windowWidth, height: windowHeight } = useWindowSize();
const { width: menuWidth, height: menuHeight } = useElementBounding(menuRef);

const calculatePosition = (x, y, menuW, menuH, windowW, windowH) => {
  const PADDING = 16;
  // Initial position
  let left = x;
  let top = y;
  // Boundary checks
  const isOverflowingRight = left + menuW > windowW - PADDING;
  const isOverflowingBottom = top + menuH > windowH - PADDING;
  // Adjust position if overflowing
  if (isOverflowingRight) left = windowW - menuW - PADDING;
  if (isOverflowingBottom) top = windowH - menuH - PADDING;
  return {
    left: Math.max(PADDING, left),
    top: Math.max(PADDING, top),
  };
};

const position = computed(() => {
  // O elemento e `position: fixed` mas vive dentro do `html` com zoom, entao o
  // `left` que escrevemos e desenhado a 80%. Sem `layoutPx` o menu nascia a 80%
  // do caminho ate ao cursor -- 280px de desvio num clique a x=1400.
  if (!menuRef.value)
    return { top: layoutPx(props.y), left: layoutPx(props.x) };

  const { left, top } = calculatePosition(
    props.x,
    props.y,
    menuWidth.value,
    menuHeight.value,
    windowWidth.value,
    windowHeight.value
  );

  return {
    top: layoutPx(top),
    left: layoutPx(left),
  };
});

onMounted(() => {
  isLocked.value = true;
  nextTick(() => menuRef.value?.focus());
});

const handleClose = () => {
  isLocked.value = false;
  emit('close');
};

const onMobileSheetDragStart = event => {
  if (event.pointerType === 'mouse' && event.button !== 0) return;

  dragPointerId = event.pointerId;
  dragStartY = event.clientY;
  dragStartHeight = menuRef.value.getBoundingClientRect().height;
  dragMoved = false;
  event.currentTarget.setPointerCapture(event.pointerId);
};

const onMobileSheetDragMove = event => {
  if (event.pointerId !== dragPointerId) return;

  const delta = dragStartY - event.clientY;
  if (!dragMoved && Math.abs(delta) < 4) return;

  dragMoved = true;
  setMobileSheetHeight(dragStartHeight + delta);
};

const onMobileSheetDragEnd = event => {
  if (event.pointerId !== dragPointerId) return;

  dragPointerId = null;
  if (dragMoved && mobileSheetHeight.value <= MOBILE_SHEET_DISMISS_HEIGHT) {
    handleClose();
  }
};

const resizeMobileSheetWithKeyboard = delta => {
  setMobileSheetHeight(menuRef.value.getBoundingClientRect().height + delta);
  if (mobileSheetHeight.value <= MOBILE_SHEET_DISMISS_HEIGHT) handleClose();
};

// Aceleres 2026-09-02: fechar no `blur` do contentor impedia QUALQUER campo de
// texto dentro do menu — clicar no input de pesquisa das etiquetas tirava o
// foco da caixa e o menu fechava antes da primeira tecla. `focusout` borbulha
// e traz o destino do foco em `relatedTarget`: se ele continua dentro do menu,
// nao ha nada a fechar; se sai (ou nao existe, no clique fora), fecha
// exatamente como o `blur` fechava.
const handleFocusOut = event => {
  if (props.mobileSheet) return;

  const nextFocused = event.relatedTarget;
  if (nextFocused && menuRef.value?.contains(nextFocused)) return;

  handleClose();
};

onUnmounted(() => {
  isLocked.value = false;
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      :class="
        mobileSheet
          ? 'fixed inset-0 z-[9999] flex items-end bg-black/50'
          : 'contents'
      "
      @click.self="handleClose"
    >
      <div
        ref="menuRef"
        :class="
          mobileSheet
            ? 'max-h-[calc(100dvh-0.75rem)] w-full touch-manipulation overflow-x-hidden overflow-y-auto overscroll-contain rounded-t-2xl bg-n-solid-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-2xl outline-none'
            : 'fixed z-[2147482700] cursor-pointer outline-none'
        "
        :style="mobileSheet ? mobileSheetStyle : position"
        :role="mobileSheet ? 'dialog' : 'menu'"
        :aria-modal="mobileSheet ? 'true' : undefined"
        tabindex="0"
        @focusout="handleFocusOut"
      >
        <button
          v-if="mobileSheet"
          type="button"
          class="acl-conversation-menu-sheet-handle sticky top-0 z-10 flex h-[55px] w-full cursor-ns-resize touch-none select-none items-center justify-center border-0 border-b border-solid border-n-weak bg-n-solid-2 px-3 focus-visible:outline focus-visible:outline-n-brand"
          aria-label="Arraste para ajustar a altura do menu"
          @pointerdown="onMobileSheetDragStart"
          @pointermove="onMobileSheetDragMove"
          @pointerup="onMobileSheetDragEnd"
          @pointercancel="onMobileSheetDragEnd"
          @keydown.up.prevent="resizeMobileSheetWithKeyboard(80)"
          @keydown.down.prevent="resizeMobileSheetWithKeyboard(-80)"
          @keydown.esc="handleClose"
        >
          <span aria-hidden="true" class="h-1 w-10 rounded-full bg-n-slate-6" />
        </button>
        <slot />
      </div>
    </div>
  </TeleportWithDirection>
</template>
